import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ClinicalHtmlEditor, { clinicalBodyIsEmpty } from '../clinic/ClinicalHtmlEditor';
import PrescriptionFormDialog from '../clinic/PrescriptionFormDialog';
import CertificateFormDialog from '../clinic/CertificateFormDialog';
import HealthcareMedicalReportDialog from '../HealthcareMedicalReportDialog';
import OphthalmologyRefractionTableFields from './OphthalmologyRefractionTableFields';
import {
  embedRefractionInExamOrderBody,
  extractRefractionFromExamOrderBody,
} from './ophthalmologyRefractionConstants';
import {
  emptyOphthalmologyRefraction,
  parseRefractionBody,
  refractionHasContent,
  serializeRefractionBody,
  type OphthalmologyRefractionValues,
} from './ophthalmologyRefractionConstants';
import type { EvolutionAttachedPrintable } from './evolutionWorkflowTypes';
import { canEditEvolutionPrintable } from './evolutionPrintableEditPolicy';

export interface EvolutionPrintableEditHostProps {
  target: EvolutionAttachedPrintable | null;
  onClose: () => void;
  onSave: (updated: EvolutionAttachedPrintable) => void;
  patientId: string;
  professionalId: string;
  initialPatient?: any;
  initialProfessional?: any;
}

const PLAIN_TEXT_CATEGORIES = new Set(['exam_order', 'indication']);

const EvolutionPrintableEditHost: React.FC<EvolutionPrintableEditHostProps> = ({
  target,
  onClose,
  onSave,
  patientId,
  professionalId,
  initialPatient,
  initialProfessional,
}) => {
  const [htmlBody, setHtmlBody] = useState('');
  const [plainBody, setPlainBody] = useState('');
  const [refraction, setRefraction] = useState<OphthalmologyRefractionValues>(() =>
    emptyOphthalmologyRefraction(),
  );

  const mode = useMemo(() => {
    if (!target || !canEditEvolutionPrintable(target)) return 'blocked';
    if (target.category === 'prescription' && target.linkedDocumentId) return 'prescription';
    if (target.category === 'certificate' && target.linkedDocumentId) return 'certificate';
    if (target.category === 'medical_report' && target.linkedDocumentId) return 'medical_report';
    if (target.category === 'refraction') return 'refraction';
    if (PLAIN_TEXT_CATEGORIES.has(target.category)) return 'plain';
    return 'html';
  }, [target]);

  useEffect(() => {
    if (!target) return;
    if (target.category === 'refraction') {
      setRefraction(parseRefractionBody(target.bodyText));
      setPlainBody('');
      setHtmlBody('');
      return;
    }
    if (target.category === 'exam_order') {
      const { bodyWithoutMarker, refraction: embedded } = extractRefractionFromExamOrderBody(target.bodyText);
      setPlainBody(bodyWithoutMarker);
      setRefraction(embedded && refractionHasContent(embedded) ? embedded : emptyOphthalmologyRefraction());
      setHtmlBody('');
      return;
    }
    if (PLAIN_TEXT_CATEGORIES.has(target.category)) {
      setPlainBody(target.bodyText || '');
      setHtmlBody('');
      setRefraction(emptyOphthalmologyRefraction());
      return;
    }
    setHtmlBody(target.bodyText || '');
    setPlainBody('');
    setRefraction(emptyOphthalmologyRefraction());
  }, [target]);

  const handleTextSave = () => {
    if (!target) return;
    let bodyText = target.bodyText;
    if (mode === 'refraction') {
      bodyText = serializeRefractionBody(refraction);
    } else if (mode === 'plain' && target.category === 'exam_order') {
      bodyText = embedRefractionInExamOrderBody(plainBody.trim(), refraction);
    } else if (mode === 'plain') {
      bodyText = plainBody.trim();
    } else {
      bodyText = htmlBody.trim();
    }
    onSave({ ...target, bodyText });
    onClose();
  };

  const textSaveDisabled =
    mode === 'refraction'
      ? !refractionHasContent(refraction)
      : mode === 'plain'
        ? !plainBody.trim() && !refractionHasContent(refraction)
        : clinicalBodyIsEmpty(htmlBody);

  if (!target) return null;

  if (mode === 'prescription') {
    return (
      <PrescriptionFormDialog
        open
        onClose={onClose}
        patientId={patientId}
        professionalId={professionalId}
        initialPatient={initialPatient}
        initialProfessional={initialProfessional}
        existingPrescriptionId={target.linkedDocumentId}
        existingClientKey={target.clientKey}
        onSuccess={(result) => {
          const updated = result.printables[0];
          if (updated) onSave(updated);
          onClose();
        }}
      />
    );
  }

  if (mode === 'certificate') {
    return (
      <CertificateFormDialog
        open
        onClose={onClose}
        patientId={patientId}
        professionalId={professionalId}
        initialPatient={initialPatient}
        initialProfessional={initialProfessional}
        existingCertificateId={target.linkedDocumentId}
        existingClientKey={target.clientKey}
        onSuccess={(updated) => {
          onSave(updated);
          onClose();
        }}
      />
    );
  }

  if (mode === 'medical_report') {
    return (
      <HealthcareMedicalReportDialog
        open
        onClose={onClose}
        patientId={patientId}
        patientName={initialPatient?.full_name || initialPatient?.name || 'Paciente'}
        existingReportId={target.linkedDocumentId}
        existingClientKey={target.clientKey}
        professionalId={professionalId}
        initialProfessional={initialProfessional}
        onSuccess={(updated) => {
          onSave(updated);
          onClose();
        }}
      />
    );
  }

  if (mode === 'blocked') {
    return (
      <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Edição indisponível</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {target.linkedDocumentOrigin === 'memed'
              ? 'Receita digital Memed: o documento oficial deve ser alterado na plataforma Memed.'
              : 'Este impresso não pode ser editado nesta tela.'}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
            Editar — {target.templateName}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Ajuste o conteúdo que será salvo na evolução e impresso.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.5 }}>
        {mode === 'refraction' ? (
          <OphthalmologyRefractionTableFields values={refraction} onChange={setRefraction} showNotes />
        ) : mode === 'plain' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={target.category === 'exam_order' ? 14 : 8}
              label="Texto do documento"
              value={plainBody}
              onChange={(e) => setPlainBody(e.target.value)}
              placeholder="Conteúdo que será impresso..."
            />
            {target.category === 'exam_order' ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1d4ed8' }}>
                  Refração nas observações (opcional)
                </Typography>
                <OphthalmologyRefractionTableFields
                  values={refraction}
                  onChange={setRefraction}
                  showNotes={false}
                />
              </Box>
            ) : null}
          </Box>
        ) : (
          <ClinicalHtmlEditor
            minRows={14}
            label="Texto do documento"
            value={htmlBody}
            onChange={setHtmlBody}
            placeholder="Conteúdo que será impresso..."
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={textSaveDisabled}
          onClick={handleTextSave}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
        >
          Salvar alterações
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EvolutionPrintableEditHost;
